import {
  parsePhoneNumber,
  getSupportedCallingCodes,
  getRegionCodeForCountryCode,
  getCountryCodeForRegionCode,
} from "awesome-phonenumber";
import cn from "classnames";
import countries from "i18n-iso-countries";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import * as uuid from "uuid";

import { useIsMounted } from "@hooks/useIsMounted";
import TicketsInput from "@ui-kit/TicketsInput/TicketsInput";

import s from "./PhoneInput.module.scss";

export const localeCountry = {
  // en: "US",
  // et: "EE",
  lt: "LT",
  lv: "LV",
  pl: "PL",
  // ru: "RU",
  // fi: "FI",
  // de: "DE",
};

const validPhoneCodes = getSupportedCallingCodes().sort((a, b) => a - b);

// eslint-disable-next-line react/display-name
const PhoneNumberInput = ({
  className,
  inputClassName,
  required,
  errors,
  label,
  onChange,
  value = "",
  withoutMessage = false,
  disabled = false,
  ...props
}) => {
  const { locale, locales } = useRouter();
  const isMounted = useIsMounted();
  const id = uuid.v4();
  const defaultAreaCode = 372; // Estonia

  const normalizedValue = value
    ? value.startsWith("+")
      ? value
      : `+${value}`
    : "";

  const getMobileCountryCode = () => {
    if (localeCountry[locale]) {
      const mobileCountryCode = getCountryCodeForRegionCode(
        localeCountry[locale],
      ); // for UK: 380
      return mobileCountryCode;
    }

    return null;
  };

  const parsedPhone = parsePhoneNumber(normalizedValue);

  const findCode = validPhoneCodes.find((code) =>
    normalizedValue.startsWith(`+${code}`),
  );

  const phoneNumber =
    parsedPhone.number?.input?.replace(`+${findCode}`, "") ||
    normalizedValue.replace(`+${findCode}`, "");

  const [phoneAreaCode, setPhoneAreaCode] = useState(
    () =>
      parsedPhone.countryCode ||
      findCode ||
      getMobileCountryCode() ||
      defaultAreaCode,
  );

  const countryCode = getRegionCodeForCountryCode(phoneAreaCode);

  const getCountryName = (countryCode) => {
    return countries.getName(countryCode, locale, { select: "official" }); // 'en' for English language
  };

  const getFullNumber = (areaCode, phone) => `+${areaCode}${phone}`;

  useEffect(() => {
    locales.forEach((locale) => {
      try {
        countries.registerLocale(
          require(`i18n-iso-countries/langs/${locale}.json`),
        );
      } catch (error) {
        console.error(`Could not load locale ${locale}: ${error.message}`);
      }
    });
  }, []);

  useEffect(() => {
    if (!normalizedValue) {
      setPhoneAreaCode(getMobileCountryCode() || defaultAreaCode);
    }
  }, [locale]);

  useEffect(() => {
    findCode && setPhoneAreaCode(findCode);
  }, [findCode]);

  return (
    <div
      className={cn(
        s.phoneNumberInput,
        withoutMessage && s.withoutMessage,
        errors && s.error,
        disabled && s.disabled,
        className,
      )}
    >
      {label && <label htmlFor={id}>{`${label}${required ? " *" : ""}`}</label>}
      <div className={s.phone}>
        <div className={s.country}>
          {isMounted && (
            <ReactCountryFlag
              svg
              className={s.flag}
              countryCode={countryCode}
            />
          )}

          <select
            className={s.listOfCountries}
            value={phoneAreaCode}
            disabled={disabled}
            onChange={(e) => {
              const value = e.target.value;
              onChange(getFullNumber(value, phoneNumber), {
                phoneAreaCode: value,
                phoneNumber: phoneNumber,
              });
            }}
          >
            {getSupportedCallingCodes()
              .sort((a, b) => a - b)
              .map((country, key) => {
                const areaCode = getRegionCodeForCountryCode(country);
                const countryName = getCountryName(areaCode);
                return (
                  countries.isValid(areaCode) && (
                    <option key={key} value={country}>
                      + {country}
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                      {countryName}
                    </option>
                  )
                );
              })}
          </select>
        </div>
        <TicketsInput
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, "");
            onChange(getFullNumber(phoneAreaCode, value), {
              phoneAreaCode,
              phoneNumber: value,
            });
          }}
          type="text"
          value={phoneNumber}
          className={s.input}
          withoutMessage
          id={id}
          {...props}
        />
      </div>
      {errors && <div className={s.message}>{errors}</div>}
    </div>
  );
};

export default PhoneNumberInput;
